/**
 * Owner: Masa Bayu
 * File: universal-text-node.ts
 * Date:Time: 2026-10-05 19:35
 * Description: Fasa 31 · Lexical TextNode · fontFamily/size/weight/color · px DOM.
 */

import {
  $applyNodeReplacement,
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

export type SerializedUniversalTextNode = Spread<
  {
    fontFamily: string;
    fontSize: number;
    fontWeight: number;
    color: string;
  },
  SerializedTextNode
>;

/** @noInheritDoc */
export class UniversalTextNode extends TextNode {
  __fontFamily: string;
  __fontSize: number;
  __fontWeight: number;
  __color: string;

  override $config() {
    return this.config('universal-text', { extends: TextNode });
  }

  static override clone(node: UniversalTextNode): UniversalTextNode {
    const cloned = new UniversalTextNode(node.__text, node.__key);
    cloned.__fontFamily = node.__fontFamily;
    cloned.__fontSize = node.__fontSize;
    cloned.__fontWeight = node.__fontWeight;
    cloned.__color = node.__color;
    return cloned;
  }

  static override importJSON(
    serializedNode: SerializedUniversalTextNode,
  ): UniversalTextNode {
    return $createUniversalTextNode().updateFromJSON(serializedNode);
  }

  constructor(text = '', key?: NodeKey) {
    super(text, key);
    this.__fontFamily = 'sans-serif';
    this.__fontSize = 12;
    this.__fontWeight = 400;
    this.__color = '#000000';
  }

  getFontFamily(): string {
    return this.__fontFamily;
  }

  getFontSize(): number {
    return this.__fontSize;
  }

  getFontWeight(): number {
    return this.__fontWeight;
  }

  getColor(): string {
    return this.__color;
  }

  setFontFamily(fontFamily: string): this {
    const self = this.getWritable();
    self.__fontFamily = fontFamily;
    return self;
  }

  setFontSize(fontSize: number): this {
    const self = this.getWritable();
    self.__fontSize = fontSize;
    return self;
  }

  setFontWeight(fontWeight: number): this {
    const self = this.getWritable();
    self.__fontWeight = fontWeight;
    return self;
  }

  setColor(color: string): this {
    const self = this.getWritable();
    self.__color = color;
    return self;
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    this.applyTypography(dom);
    return dom;
  }

  override updateDOM(
    prevNode: this,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const updated = super.updateDOM(prevNode, dom, config);
    if (
      prevNode.__fontFamily !== this.__fontFamily ||
      prevNode.__fontSize !== this.__fontSize ||
      prevNode.__fontWeight !== this.__fontWeight ||
      prevNode.__color !== this.__color
    ) {
      this.applyTypography(dom);
    }
    return updated;
  }

  override exportJSON(): SerializedUniversalTextNode {
    return {
      ...super.exportJSON(),
      type: 'universal-text',
      version: 1,
      fontFamily: this.__fontFamily,
      fontSize: this.__fontSize,
      fontWeight: this.__fontWeight,
      color: this.__color,
    };
  }

  override updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedUniversalTextNode>,
  ): this {
    return super
      .updateFromJSON(serializedNode)
      .setFontFamily(serializedNode.fontFamily ?? 'sans-serif')
      .setFontSize(serializedNode.fontSize ?? 12)
      .setFontWeight(serializedNode.fontWeight ?? 400)
      .setColor(serializedNode.color ?? '#000000');
  }

  private applyTypography(dom: HTMLElement): void {
    dom.style.fontFamily = this.__fontFamily;
    // contract-change-31-px · unify with QxPageRenderer (was pt)
    dom.style.fontSize = `${this.__fontSize}px`;
    dom.style.fontWeight = String(this.__fontWeight);
    dom.style.color = this.__color;
  }
}

export function $createUniversalTextNode(text = ''): UniversalTextNode {
  return $applyNodeReplacement(new UniversalTextNode(text));
}

export function $isUniversalTextNode(
  node: LexicalNode | null | undefined,
): node is UniversalTextNode {
  return node instanceof UniversalTextNode;
}
